import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useApiClient } from "@/infrastructure/rest";
import { BetrokkenTreinEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import {
    BetrokkenTreinDto,
    BusEvacuatieAanvraagDto,
    BusEvacuatieStatusPatchDto,
    BusEvacuatieWijzigingDto,
    CreateBetrokkenTreinRequestDto,
    TreinEvacuatieDto,
    UpdateBetrokkenTreinRequestDto,
} from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BETROKKEN_TREINEN_QUERY_KEY } from "./constants.ts";
import { BetrokkenTrein, BusEvacuatieStatus } from "./domain";

export function useGetDossierBetrokkenTreinenById(id: string, select?: (data: BetrokkenTrein[]) => BetrokkenTrein[]) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, id, BETROKKEN_TREINEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<BetrokkenTreinDto[]>(`/dossiers/${id}/betrokken-treinen`);
            return data?.map((dto) => BetrokkenTrein.fromDto(dto)).sort(BetrokkenTrein.sort) ?? [];
        },
        select: select,
    });
}

export type PatchBetrokkenTrein = UpdateBetrokkenTreinRequestDto & {
    dossierId: string;
    treinId: string;
};

export function usePatchBetrokkenTrein() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...data }: PatchBetrokkenTrein) => {
            await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error patching betrokken trein:", error);
        },
    });
}

export type PostBetrokkenTrein = CreateBetrokkenTreinRequestDto & {
    dossierId: string;
};

export function usePostBetrokkenTrein() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...data }: PostBetrokkenTrein) => {
            await apiClient.post(`/dossiers/${dossierId}/betrokken-treinen`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting betrokken trein:", error);
        },
    });
}

export type PutBusEvacuatieEta = {
    dossierId: string;
    treinId: number;
    etas: string[];
};

export function usePutBusEvacuatieEtas() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...data }: PutBusEvacuatieEta) => {
            await apiClient.put(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/bus-evacuatie`, data.etas);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error adding ETA for bus-evacuatie", error);
        },
    });
}

export type DeleteBetrokkenTrein = {
    dossierId: string;
    treinId: number;
};

export function useDeleteBetrokkenTrein() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId }: DeleteBetrokkenTrein) => {
            await apiClient.delete(`/dossiers/${dossierId}/betrokken-treinen/${treinId}`);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error deleting betrokken trein:", error);
        },
    });
}

export type PostBusEvacuatie = BusEvacuatieAanvraagDto & {
    dossierId: string;
    treinId: number;
};

export function usePostBusEvacuatie() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...data }: PostBusEvacuatie) => {
            await apiClient.post(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/bus-evacuatie`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting bus evacuatie:", error);
        },
    });
}

export type PatchBusEvacuatie = BusEvacuatieWijzigingDto & {
    dossierId: string;
    treinId: number;
};

export function usePatchBusEvacuatie() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...data }: PatchBusEvacuatie) => {
            await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/bus-evacuatie`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error patching bus evacuatie:", error);
        },
    });
}

export type PatchBusEvacuatieStatus = BusEvacuatieStatusPatchDto & {
    dossierId: string;
    treinId: number;
};

export function usePatchBusEvacuatieStatus() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...data }: PatchBusEvacuatieStatus) => {
            await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/bus-evacuatie/status`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error patching bus evacuatie status:", error);
        },
    });
}

type BusEvacuatieAfronden = PatchBusEvacuatie & { currentStatus: BusEvacuatieStatus };

export function useBusEvacuatieAfronden() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, currentStatus, ...data }: BusEvacuatieAfronden) => {
            // only mutate status if it is not yet afgerond. After afronding, RV can still open the dialog and change data
            if (currentStatus !== "Afgerond") {
                await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/bus-evacuatie/status`, { status: "Afgerond" });
            }
            await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/bus-evacuatie`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error afronden busevacuatie:", error);
        },
    });
}

type PostTreinEvacuatieRequest = Omit<TreinEvacuatieDto, "status"> & {
    dossierId: string;
    treinId: number;
};

export function usePostTreinEvacuatie() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...payload }: PostTreinEvacuatieRequest) => {
            await apiClient.post(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/trein-evacuatie`, payload);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting treinevacuatie:", error);
        },
    });
}

type PatchTreinEvacuatieRequest = Omit<TreinEvacuatieDto, "status"> & {
    dossierId: string;
    treinId: number;
};

export function usePatchTreinEvacuatie() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId, ...payload }: PatchTreinEvacuatieRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/trein-evacuatie`, payload);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error patching treinevacuatie:", error);
        },
    });
}

type PatchTreinEvacuatieStatusRequest = {
    dossierId: string;
    treinId: number;
};

export function usePatchTreinEvacuatieStatus() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, treinId }: PatchTreinEvacuatieStatusRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/betrokken-treinen/${treinId}/trein-evacuatie/status`, {
                status: "Geannuleerd",
            });
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error patching treinevacuatie to status=Geannuleerd:", error);
        },
    });
}

export function useRealtimeBetrokkenTreinen() {
    const { handleEvent } = useUpdateDossierPropertyQueriesData(BETROKKEN_TREINEN_QUERY_KEY);

    useRealtimeTopic<BetrokkenTreinEvent>("betrokkenTreinen", {
        onMessage: (message) => {
            const { betrokkenTrein, dossierId, reason } = message;
            handleEvent(dossierId, betrokkenTrein.id, reason, BetrokkenTrein.fromDto(betrokkenTrein));
        },
    });
}
